import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import isFunction from 'lodash/isFunction'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useTranslation } from '@/libs/next-lang'
import { AuthorizedButton } from '@/components/Authorized'
import globalStore from '@/globalStore'
import MemberGrayscalePermissions, {
  isGrayOrdinaryMembers,
  isAllowGrayProbationaryMembers,
} from '@/components/MemberGrayscalePermissions'
import { getLoginStatus, getDynamicPath } from '@/utils'
import config from '@/utils/config'
import MemberArea from '@/components/MemberArea'
import ProbationaryMemberArea, { useSucessResultModal } from '@/components/ProbationaryMemberArea'
import { userActionRecord, ACTION_TYPE } from '@/utils/userActionRecord'
import CustomerService from '@/components/CustomerService'
import useCampaignsGoBackModal from '@/components/useCampaignsGoBackModal'
import { HOME_PATH } from '@/constants'
import useBenefitCollectionModal from './useBenefitCollectionModal'
import useMemberAutoReceive from './useMemberAutoReceive'
import styles from './index.module.less'

const LoginBtn = dynamic(() => import('./LoginBtn'), { ssr: false })

const Index = () => {
  const globalMobx = useLocalObservable(() => globalStore)
  const history = useRouter()
  const pathname = history.pathname
  const { t, i18n } = useTranslation(['header'])
  const { showModal: showSucessResultModal, render: renderSucessResultModal } =
    useSucessResultModal()

  const {
    loginModalVisible,
    routes,
    userInfo,
    activeCorporationId,
    activeCorporationName,
    corporationList,
    authorizedList,
  } = globalMobx

  const [newRouter, setNewRouter] = useState([])

  const { showModal: showBenefitCollectionModal, render: renderBenefitCollectionModal } =
    useBenefitCollectionModal()

  const { showModal: showMemberAutoReceive, render: renderMemberAutoReceive } =
    useMemberAutoReceive({
      updateMemberShow: (v: number) => globalMobx?.updateGlobalStore?.('memberReceiveShow', v),
    })

  const isMemberGrayscalePermissions = useMemo(() => {
    if (userInfo?.isBindSSO) {
      return false
    }
    if (userInfo?.corporationId) {
      return false
    }
    return true
  }, [])

  const { goBackShowModal, goBackRenderModal } = useCampaignsGoBackModal({})
  // 下单页面、模特换脸其他操作：统一处理不同点位需要做的事情
  const campaignsNewGoBackChange = (callback: any) => {
    if (['/campaigns/new', '/ai-generator'].includes(history.pathname)) {
      goBackShowModal(callback)
    } else {
      callback()
    }
  }

  const { isBindSSO, name, isPersonal, dynamicPath } = useMemo(() => {
    // 切换身份时或切换身份再次登录，昵称显示主体名称；
    const nameRecord = find(globalMobx.corporationList, {
      corporationId: globalMobx.activeCorporationId,
    })
    return {
      name: activeCorporationName || nameRecord?.corporationName || userInfo?.nickName, // 头像下拉名称
      isPersonal: globalMobx.getIsPersonal(), // 是否个人呢
      dynamicPath: getDynamicPath(authorizedList, 'MANAGEMENT'),
      isBindSSO: userInfo?.isBindSSO,
    }
  }, [userInfo, activeCorporationId, activeCorporationName, corporationList, authorizedList])

  /**
   * 路由跳转
   * @param item
   */
  const handleRouteChange = (item: any) => {
    if (item.path === '/home') {
      window.location.href = '/'
      return
    }
    // 此处需要重新获取登录态进行判断
    if (!getLoginStatus() && !config.unAuthPages.includes(item.path)) {
      globalMobx.loginModalToggle(true, () => {
        globalMobx.targetPathUrl = item.path
      })
    } else {
      // 带二级菜单需按照权限设置处理，防止item.path 被禁用
      if (item.path === pathname) {
        return
      }
      let dp = item.path
      if (['MEDIA', 'DATA_CENTER'].includes(item.code)) {
        dp = getDynamicPath(globalMobx.authorizedList, item.code) || item.path
      }
      history.push(dp)
      // 顶部菜单埋点
      userActionRecord({
        actionType: ACTION_TYPE.DEMAND_INDEX_TAB_CLICK,
        actionTargetName: item.name,
      })
    }
  }

  /**
   * 登录
   */
  const handleLogin = () => {
    globalMobx.loginModalToggle(true, () => (globalMobx.targetPathUrl = pathname))
  }

  /**
   * 退出登录
   */
  const handleLoginOut = () => {
    // if (!config.unAuthPages.includes(pathname)) {
    //   history.push(HOME_PATH)
    // }
    campaignsNewGoBackChange(() => {
      globalMobx.loginOut(history, (locale: string) => {
        i18n.changeLanguage(locale)
      })
    })
  }

  // 头像下拉菜单
  const dropdownMenus = useMemo(() => {
    // 企业管理在菜单入口跳转链接动态
    const managementItem = dynamicPath
      ? [
          {
            path: dynamicPath,
            translateKey: 'header:t9',
            icon: 'icon-qiyeguanli',
            label: '企业管理',
            sso: false,
          },
        ]
      : []
    // 个人
    const item = isPersonal
      ? [
          {
            path: '/finance/record',
            translateKey: 'header:t10',
            icon: 'icon-qianbao',
            label: '财务管理',
            sso: false,
          },
        ]
      : managementItem

    // 添加福利兑换入口
    if (isPersonal && isMemberGrayscalePermissions) {
      item.push({
        // @ts-ignore
        action: () => showBenefitCollectionModal(),
        translateKey: 'header:t14',
        icon: 'icon-cardPackage',
        label: '福利兑换',
        sso: false,
      })
    }
    // 当企业管理下面都没有权限时，不展示入口
    const defaultMenu: any = [
      {
        path: '/my/info',
        translateKey: 'header:t11',
        icon: 'icon-gerenxinxi',
        label: '个人账户',
        sso: true,
      },
      {
        path: '/collect?tab=0',
        translateKey: 'header:t12',
        icon: 'icon-shoucang',
        label: '我的收藏',
        sso: true,
      },
      ...item
    ].filter(f => !!f)
    // if (isBindSSO) {
    //   defaultMenu.push({
    //     path: '/dam/space/my-space',
    //     translateKey: 'header:t37',
    //     icon: 'icon-product',
    //     label: 'DAM',
    //     sso: true,
    //   })
    // }
    return defaultMenu
  }, [isBindSSO, isPersonal, dynamicPath])

  /**
   * 渲染顶部菜单
   * @returns
   */
  useEffect(() => {
    let newList = routes
    // 专区页面不展示，模特换脸也不展示
    const aigcRecord = find(newList, item => item.path === '/ai/playground')
    if (userInfo.token) {
      // 已登录
      if (isPersonal) {
        const creativeMallRecord = find(newList, item => item.path === '/creative/mall')
        // 有商城在第5个，没有商城在第3位，这里数字是实际数组里面的位置，有隐藏的菜单
        // eslint-disable-next-line no-nested-ternary
        const sliceIndex = creativeMallRecord ? (aigcRecord ? 5 : 4) : aigcRecord ? 4 : 3
        const startList = newList.slice(0, sliceIndex)
        const endList = newList.slice(sliceIndex)
        newList = [
          ...startList,
          // {
          //   path: '/order',
          //   translateKey: 'header:t5',
          //   name: '我的订单',
          //   exact: true,
          //   showItem: true,
          //   code: '',
          //   exclude: ['/order', '/order/[id]'],
          // },
          ...endList,
        ]
      }
      // if ((!isPersonal) && aigcRecord) {
      //   // sso 用户\企业用户不展示
      //   const filterList = newList.filter(
      //     (item: any) => !['/ai/playground', '/ai-generator'].includes(item.path),
      //   )
      //   newList = filterList
      //   aigcRecord = undefined
      // }
    } else {
      // 未登录
      if (!aigcRecord) {
        // 未登录展示AIGC
        const aiStartList = newList.slice(0, 1)
        const aiEndList = newList.slice(1)
        newList = [
          ...aiStartList,
          {
            path: '/ai/playground',
            name: 'AIGC',
            exact: true,
            showItem: true,
            code: '',
            exclude: ['/ai/playground', '/ai-generator'],
          },
          ...aiEndList,
        ]
      }
      // 企业
      if (!isPersonal) {
        const filterList = newList.filter((item: any) => item.path !== '/order')
        newList = filterList
      }
    }
    setNewRouter(newList)
  }, [userInfo.token, isPersonal, routes])

  // 是否普通会员身份
  const isGOM: boolean = isGrayOrdinaryMembers() || false
  // 是否允许领取试用期会员
  const isAGPM: boolean = isAllowGrayProbationaryMembers() || false

  useEffect(() => {
    if (
      !isEmpty(userInfo) &&
      isPersonal &&
      userInfo?.isBindTag &&
      isGOM &&
      isAGPM &&
      !loginModalVisible
    ) {
      // 自动领会员，满足条件: 个人用户 & 完成了用户引导 & 是普通会员身份 & 允许领取试用期会员
      showMemberAutoReceive()
    }
  }, [isGOM, isAGPM, userInfo, isPersonal, loginModalVisible])

  return (
    <>
      <div className={classNames('flex flex-row items-center justify-between', styles.header)}>
        <div className={classNames('flex flex-row items-center', styles.left)}>
          <div
            style={{ fontSize: 0, width: 115, height: 32 }}
            className={classNames('cursor-pointer', styles['logo-wrap'])}
            onClick={() => {
              campaignsNewGoBackChange(() => {
                // history.push(HOME_PATH)
                window.location.href = '/'
              })
            }}
          >
            <img
              src="https://static.creativebooster.com/common/logo-w.png"
              alt="logo"
              style={{ display: 'block', width: '100%', height: '100%' }}
            />
          </div>
          <div
            className={classNames(styles['nav-wrap'], 'flex flex-row items-center justify-center')}
          >
            <ul className={classNames('flex flex-row items-center', styles.nav)}>
              {config.hideMenuList.includes(pathname)
                ? null
                : newRouter?.map((item: any) =>
                    item.showItem ? (
                      <AuthorizedButton useOpacity={false} key={item.name} code={item.code}>
                        <li
                          key={item.path}
                          className={classNames(styles['nav-item'], {
                            [styles.active]: item.exclude
                              ? item.exclude.includes(pathname)
                              : pathname === item.path,
                          })}
                          onClick={() => handleRouteChange(item)}
                        >
                          {t(item.translateKey)}{' '}
                          {item.path === '/ai/playground' && (
                            <img src="/images/aigc/hot.svg" className={styles.hot} />
                          )}
                        </li>
                      </AuthorizedButton>
                    ) : null,
                  )}
            </ul>
          </div>
        </div>
        <div className="flex items-center ml-10">
          {!config.hideMenuList.includes(pathname) && (
            <>
              <CustomerService style={{ padding: '3px 10px' }} classNames="customer-service" />
              <div style={{ marginRight: 24 }}>
                {isEmpty(userInfo) ? (
                  // <MemberArea
                  //   onClick={() => {
                  //     // 未登录时，打开登录弹窗
                  //     handleLogin()
                  //   }}
                  // />
                  <ProbationaryMemberArea
                    onClick={() => {
                      // 未登录时，打开登录弹窗
                      handleLogin()
                    }}
                    onSucess={() => {
                      showSucessResultModal({
                        manyTimes: 1,
                      })
                    }}
                  />
                ) : (
                  <MemberGrayscalePermissions>
                    {isGOM ? (
                      isAGPM ? (
                        <ProbationaryMemberArea
                          onSucess={() =>
                            showSucessResultModal({
                              manyTimes: 1,
                            })
                          }
                        />
                      ) : (
                        <MemberArea onClick={() => history.push('/pricing')} />
                      )
                    ) : (
                      <MemberArea
                        grade={userInfo?.memberLevel}
                        validityPeriod={userInfo?.expireTime}
                        onClick={() => history.push('/pricing')}
                      />
                    )}
                  </MemberGrayscalePermissions>
                )}
              </div>
            </>
          )}
          <LoginBtn
            name={name}
            isPersonal={isPersonal}
            menus={dropdownMenus}
            userInfo={userInfo}
            onChange={({ path, action }) => {
              if (path) {
                campaignsNewGoBackChange(() => {
                  history.push(path)
                })
              } else if (isFunction(action)) {
                action()
              }
            }}
            onLogin={handleLogin}
            onLoginOut={handleLoginOut}
            campaignsNewGoBackChange={campaignsNewGoBackChange}
          />
        </div>
      </div>
      {renderBenefitCollectionModal()}
      {renderSucessResultModal()}
      {/* 返回二次确认 */}
      {goBackRenderModal()}
      {renderMemberAutoReceive()}
    </>
  )
}
export default observer(Index)
